import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { errorCatch } from './errorCatcher.reducer';
import { RootState } from '../app/rootReducer';

export enum ComplianceRegions {
  US = 'US',
  AU = 'AU',
  GB = 'GB',
  Global = 'Global'
};

export const getComplianceRegions = (countryIso2: string, enhancedBillingEnabledCountries: string[]) => {
  const country = countryIso2.toUpperCase();
  if (country === ComplianceRegions.US || country === ComplianceRegions.AU || country === ComplianceRegions.GB ) {
    return [country as ComplianceRegions, ComplianceRegions.Global];
  }
  return enhancedBillingEnabledCountries.includes(country) 
    ? [ComplianceRegions.Global] 
    : [ComplianceRegions.Global, ComplianceRegions.US];
}

export type Client = {
  companyId: number;
  aionOrganizationId: string;
  companyName: string;
};

export type HiringClientsDataType = {
  '@odata.count': number;
  value: Client[];
};

export type InitState = {
  hiringClients: {
    status: string;
    data: ResponseHiringClientsType;
  };
  hiringClientByReferral: {
    status: string;
    data: Client | null;
  };
};

export const initialState = {
  hiringClients: {
    status: '',
    data: {
      options: [],
      hasMore: true,
      additional: { page: 1 }
    }
  },
  hiringClientByReferral: {
    status: '',
    data: null
  }
} as InitState;

type RequestParams = {
  search?: string;
  page: number;
};

type ResponseHiringClientsType = {
  options: Client[];
  hasMore: boolean;
  additional: {
    page: number;
  };
};

export const getHiringClients = createAsyncThunk(
  'hiringClient/getHiringClients',
  async ({ search = '', page }: RequestParams, { dispatch, getState }): Promise<ResponseHiringClientsType> => {
    try {
      search = search.replace(/'/g, "''");
      const top = 30;
      const { countryIso2 } = (getState() as RootState).companyData.signUpData;
      const { enhancedBillingEnabledCountries } = (getState() as RootState).countryConfig;
      const complianceRegions = getComplianceRegions(countryIso2, enhancedBillingEnabledCountries);
      const complianceRegionsQuery = complianceRegions.map(region => `complianceRegions=${region}`).join('&');
      const { data } = await Axios.get<HiringClientsDataType>(`/api/v1/billing/HiringClients?${complianceRegionsQuery}`, {
        params: {
          $filter: `startswith(companyName, '${search}')`,
          $top: top,
          $skip: (page - 1) * top,
          $orderby: 'companyName'
        }
      });

      const total = data['@odata.count'];

      return {
        options: data.value,
        hasMore: Math.ceil(total / top) > page,
        additional: { page: page + 1 }
      };
    } catch (error) {
      dispatch(errorCatch({ place: 'hiringClient/getHiringClients.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

export const getHiringClientByOrgId = createAsyncThunk(
  'hiringClient/getHiringClientByOrgId',
  async (aionOrganizationId: string, { dispatch }): Promise<Client | null> => {
    try {
      const allComplianceRegions = Object.values(ComplianceRegions);
      const complianceRegionsQuery = allComplianceRegions.map(region => `complianceRegions=${region}`).join('&');
      const { data } = await Axios.get<HiringClientsDataType>(`/api/v1/billing/HiringClients?${complianceRegionsQuery}`, {
        params: {
          $filter: `aionOrganizationId eq ${aionOrganizationId}`,
          $top: 1
        }
      });
      return data.value[0] ?? null;
    } catch (error) {
      dispatch(errorCatch({ place: 'hiringClient/getHiringClientByOrgId.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

const slice = createSlice({
  name: 'hiringClient',
  initialState,
  reducers: {},
  extraReducers: {
    [getHiringClients.pending.toString()]: state => {
      state.hiringClients.status = 'loading';
    },
    [getHiringClients.fulfilled.toString()]: (state, action) => {
      state.hiringClients.data = action.payload;
      state.hiringClients.status = 'success';
    },
    [getHiringClients.rejected.toString()]: state => {
      state.hiringClients.status = 'rejected';
    },
    [getHiringClientByOrgId.pending.toString()]: state => {
      state.hiringClientByReferral.status = 'loading';
      state.hiringClientByReferral.data = null;
    },
    [getHiringClientByOrgId.fulfilled.toString()]: (state, action) => {
      state.hiringClientByReferral.data = action.payload;
      state.hiringClientByReferral.status = 'success';
    },
    [getHiringClientByOrgId.rejected.toString()]: state => {
      state.hiringClientByReferral.status = 'rejected';
      state.hiringClientByReferral.data = null;
    }
  }
});

export default slice.reducer;
